/* X-All header chrome behaviour. Progressive: the chrome renders and every
   panel opens without this file (the panels are <details>, the drawer trigger
   is a link to the drawer's own id fallback, the bar shows its first slide).
   This adds the measured height, the scroll state, rotation, hover-open, and
   the drawer.

   Ported from SiteHeader.jsx / MobileDrawer.jsx in design project 0ff9957f,
   with one deliberate change: page padding is NOT written to body.style here.
   It comes from --x-h-chrome in CSS, which falls back to a token calc, so there
   is no unpadded first paint. This file only refines --x-h-header. */
class XallHeader extends HTMLElement {
  connectedCallback() {
    document.documentElement.classList.add('x-js');

    this.chrome = this.querySelector('.x-hdr');
    this.fixed = this.chrome && !this.chrome.classList.contains('x-hdr--static');
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.measure = this.measure.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onHashChange = this.updateRailActiveState.bind(this);

    this.initMeasure();
    this.initPanels();
    this.initAnnouncements();
    this.initDrawer();
    this.initRailActiveState();

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('hashchange', this.onHashChange);
    if (this.observer) this.observer.disconnect();
    this.pauseAnnouncements();
    if (this.drawer?.classList.contains('x-on')) removeTrapFocus();
    document.body.classList.remove('x-sc', 'x-mopen');
  }

  /* ── measured height ── */
  initMeasure() {
    if (!this.fixed) return;
    this.height = 0;
    this.measure();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    // Re-measure when late stylesheets, webfonts or an extra promo row change
    // the bar's height. The CSS fallback covers the window before this runs.
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(this.measure);
      this.observer.observe(this.chrome);
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.measure);
  }

  measure() {
    if (!this.chrome) return;
    const h = this.chrome.offsetHeight;
    if (!h || h === this.height) return;
    this.height = h;
    document.documentElement.style.setProperty('--x-h-header', `${h}px`);
  }

  onScroll() {
    document.body.classList.toggle('x-sc', window.scrollY > this.height);
  }

  /* ── disclosure panels: native <details>, hover-opened on pointer devices ── */
  initPanels() {
    this.panels = Array.from(this.querySelectorAll('[data-x-dd]'));
    this.panels.forEach((dd) => {
      dd.addEventListener('toggle', () => {
        if (dd.open) {
          this.panels.forEach((other) => other !== dd && this.closePanel(other));
          requestAnimationFrame(() => dd.classList.add('x-on'));
        } else {
          dd.classList.remove('x-on');
        }
      });
      // Hover intent, pointer devices only. The panel's ::before bridges the
      // 26px gap so crossing it does not count as leaving.
      dd.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        dd.open = true;
      });
      dd.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        this.closePanel(dd);
      });
    });
  }

  closePanel(dd) {
    if (!dd.open) return;
    dd.classList.remove('x-on');
    if (this.reduced.matches) {
      dd.open = false;
      return;
    }
    // Let the fade finish before the UA hides the content.
    clearTimeout(dd.xallTimer);
    dd.xallTimer = setTimeout(() => {
      dd.open = false;
    }, 220);
  }

  closeAllPanels() {
    if (this.panels) this.panels.forEach((dd) => this.closePanel(dd));
  }

  /* ── announcement rotation ── */
  initAnnouncements() {
    this.slides = Array.from(this.querySelectorAll('.x-bar .x-sl'));
    if (this.slides.length < 2 || this.reduced.matches) return;
    this.announcementBar = this.querySelector('.x-bar');
    this.announcementSpeed = (parseInt(this.dataset.rotateSpeed, 10) || 5) * 1000;
    this.announcementIndex = 0;
    this.announcementHovered = false;
    this.announcementFocused = false;

    this.announcementBar.addEventListener('mouseenter', () => {
      this.announcementHovered = true;
      this.pauseAnnouncements();
    });
    this.announcementBar.addEventListener('mouseleave', () => {
      this.announcementHovered = false;
      this.startAnnouncements();
    });
    this.announcementBar.addEventListener('focusin', () => {
      this.announcementFocused = true;
      this.pauseAnnouncements();
    });
    this.announcementBar.addEventListener('focusout', (event) => {
      if (event.relatedTarget && this.announcementBar.contains(event.relatedTarget)) return;
      this.announcementFocused = false;
      this.startAnnouncements();
    });

    this.startAnnouncements();
  }

  startAnnouncements() {
    if (this.timer || this.announcementHovered || this.announcementFocused) return;
    this.timer = setInterval(() => {
      this.slides[this.announcementIndex].classList.remove('x-on');
      this.announcementIndex = (this.announcementIndex + 1) % this.slides.length;
      this.slides[this.announcementIndex].classList.add('x-on');
    }, this.announcementSpeed);
  }

  pauseAnnouncements() {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  /* ── explore rail location state ── */
  initRailActiveState() {
    this.railLinks = Array.from(this.querySelectorAll('[data-x-rail-link]'));
    if (!this.railLinks.length) return;
    this.updateRailActiveState();
    window.addEventListener('hashchange', this.onHashChange);
  }

  updateRailActiveState() {
    if (!this.railLinks) return;
    this.railLinks.forEach((link) => {
      const href = link.getAttribute('href');
      const url = href ? new URL(href, window.location.href) : null;
      const isActive =
        url &&
        url.origin === window.location.origin &&
        url.pathname === window.location.pathname &&
        url.hash === window.location.hash;

      link.classList.toggle('x-on', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'location');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  /* ── mobile drawer ── */
  initDrawer() {
    this.drawer = document.querySelector('[data-xall-drawer]');
    this.toggle = this.querySelector('[data-xall-drawer-toggle]');
    if (!this.drawer || !this.toggle) return;

    this.toggle.addEventListener('click', (e) => {
      e.preventDefault();
      this.openDrawer();
    });
    this.drawer.querySelectorAll('[data-xall-drawer-close]').forEach((el) => {
      el.addEventListener('click', () => this.closeDrawer());
    });
    // Any link closes it, matching the design.
    this.drawer.querySelectorAll('a[href]').forEach((el) => {
      el.addEventListener('click', () => this.closeDrawer());
    });
  }

  openDrawer() {
    this.drawer.classList.add('x-on');
    document.body.classList.add('x-mopen');
    this.toggle.setAttribute('aria-expanded', 'true');
    this.returnFocus = document.activeElement;
    const panel = this.drawer.querySelector('.x-pnl');
    const close = this.drawer.querySelector('.x-cl');
    if (panel && close) trapFocus(panel, close);
  }

  closeDrawer() {
    if (!this.drawer || !this.drawer.classList.contains('x-on')) return;
    this.drawer.classList.remove('x-on');
    document.body.classList.remove('x-mopen');
    this.toggle.setAttribute('aria-expanded', 'false');
    removeTrapFocus(this.returnFocus);
    this.returnFocus = null;
  }

  /* ── document-level handlers ── */
  onDocumentClick(e) {
    if (!e.target.closest('[data-x-dd]')) this.closeAllPanels();
  }

  onKeydown(e) {
    if (e.key !== 'Escape') return;
    this.closeAllPanels();
    this.closeDrawer();
  }

  onResize() {
    if (window.innerWidth > 820) this.closeDrawer();
    this.measure();
  }
}

if (!customElements.get('xall-header')) customElements.define('xall-header', XallHeader);
